import { CheckIcon } from '@heroicons/react/24/outline';
import type { RefObject } from 'react';
import type { ResolvedFilterGroup, ResolvedFilterSection } from './filterDropdownSections';
import { cx } from './utils';

export interface FilterDropdownOptionListProps {
    listboxId: string;
    /** Accessible name for the listbox - the pill's label. */
    label: string;
    groups: readonly ResolvedFilterGroup[];
    activeIndex: number;
    /** DOM id for a flattened option index, matched by `aria-activedescendant`. */
    optionId: (index: number) => string;
    onSelect: (section: ResolvedFilterSection, value: string) => void;
    /** Attached to the active row so it can be scrolled into view. */
    activeOptionRef: RefObject<HTMLButtonElement>;
    multiselectable: boolean;
}

/**
 * The option rows of a filter dropdown, flat or grouped. Rows are `role`
 * `option` inside one listbox even when grouped, so arrow-key navigation walks
 * a single continuous index across section boundaries.
 */
export function FilterDropdownOptionList({
    listboxId,
    label,
    groups,
    activeIndex,
    optionId,
    onSelect,
    activeOptionRef,
    multiselectable
}: FilterDropdownOptionListProps) {
    return (
        <div
            id={listboxId}
            role="listbox"
            aria-label={label}
            aria-multiselectable={multiselectable}
            className="overflow-y-auto pb-1 max-h-64"
        >
            {groups.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400 text-center">No matches</div>
            ) : (
                groups.map((group) => (
                    <div
                        key={group.section.key}
                        role={group.section.label === null ? undefined : 'group'}
                        aria-label={group.section.label ?? undefined}
                    >
                        {group.section.label !== null && (
                            <div
                                aria-hidden
                                className="px-4 pt-2 pb-1 text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase"
                            >
                                {group.section.label}
                            </div>
                        )}
                        {group.items.map((item, itemIndex) => {
                            const index = group.offset + itemIndex;
                            const checked = group.section.selected.includes(item.value);
                            return (
                                <button
                                    key={item.value}
                                    id={optionId(index)}
                                    ref={index === activeIndex ? activeOptionRef : null}
                                    type="button"
                                    role="option"
                                    aria-selected={checked}
                                    tabIndex={-1}
                                    onClick={() => onSelect(group.section, item.value)}
                                    className={cx(
                                        'w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-100',
                                        index === activeIndex && 'bg-gray-100'
                                    )}
                                >
                                    {group.section.multiple ? (
                                        <input
                                            type="checkbox"
                                            readOnly
                                            checked={checked}
                                            tabIndex={-1}
                                            aria-hidden
                                            className="h-4 w-4 rounded border-gray-300 pointer-events-none accent-gray-900"
                                        />
                                    ) : (
                                        <CheckIcon
                                            aria-hidden
                                            className={cx(
                                                'h-4 w-4 shrink-0',
                                                checked ? 'text-gray-900' : 'text-transparent'
                                            )}
                                        />
                                    )}
                                    <span className="min-w-0 flex-1 select-none">
                                        <span className="block truncate text-gray-700">
                                            {item.label}
                                        </span>
                                        {item.sublabel && (
                                            <span className="block truncate text-[11px] text-gray-400">
                                                {item.sublabel}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                ))
            )}
        </div>
    );
}
